import { useState } from "react";

import { Box, Typography } from "@mui/material";
import { Button, Modal } from "@covertree/ui";

interface ConflictingEndorsementsModalProps {
  readonly isModalOpen: boolean;
  readonly onClose: () => void;
  onSaveAndExit: () => Promise<void>;
  onDiscardConflicting: () => Promise<void>;
}

export function ConflictingEndorsementsModal({
  isModalOpen,
  onClose,
  onSaveAndExit,
  onDiscardConflicting,
}: ConflictingEndorsementsModalProps) {
  const [pendingAction, setPendingAction] = useState<
    "saveAndExit" | "discardConflicting" | undefined
  >();
  const isLoading = Boolean(pendingAction);

  const runAction = async (
    key: "saveAndExit" | "discardConflicting",
    action: () => Promise<void>
  ) => {
    try {
      setPendingAction(key);
      await action();
    } finally {
      setPendingAction(undefined);
    }
  };

  return (
    <Modal
      disableClose={isLoading}
      open={isModalOpen}
      onClose={onClose}
      title="Conflicting Endorsements Exist"
      maxWidth={false}
      sx={{
        width: { xs: "80%", md: 720 },
        "&:focus": {
          outline: "none",
        },
      }}>
      <Typography variant="h5" sx={{ pr: 5, textAlign: "justify" }}>
        One or more existing endorsements conflict with this one. You can save
        this endorsement as a draft and review the others, or discard the
        conflicting endorsements and continue.
      </Typography>
      <Box mt={4} columnGap={3} display="flex">
        <Button
          loading={pendingAction === "saveAndExit"}
          disabled={isLoading}
          onClick={() => runAction("saveAndExit", onSaveAndExit)}>
          Save &amp; Exit
        </Button>
        <Button
          loading={pendingAction === "discardConflicting"}
          disabled={isLoading}
          onClick={() => runAction("discardConflicting", onDiscardConflicting)}
          color="error">
          Discard Conflicting
        </Button>
      </Box>
    </Modal>
  );
}

export default ConflictingEndorsementsModal;
